/**
 * Which games the viewer is browsing: everything, tabletop RPGs, or wargames. A per-viewer
 * preference kept in this browser only (it never reaches the server or other viewers), shared by
 * the sidebar switch and every browse list through a tiny external store.
 */
import { useSyncExternalStore } from 'react'
import type { GameType } from '../../shared/model'
import { useSession } from './session'

export type GameScope = 'all' | GameType
const KEY = 'codex.gameScope'
const SCOPES: readonly GameScope[] = ['all', 'ttrpg', 'wargame']

function read(): GameScope {
  if (typeof window === 'undefined') return 'all' // prerendering (scripts/prerender.tsx)
  try {
    const v = localStorage.getItem(KEY)
    return SCOPES.includes(v as GameScope) ? (v as GameScope) : 'all'
  } catch {
    return 'all'
  }
}

let current: GameScope = read()
const listeners = new Set<() => void>()

export function setGameScope(scope: GameScope): void {
  current = scope
  try {
    localStorage.setItem(KEY, scope)
  } catch {
    // Storage blocked (private window, previews): the choice still holds for this visit.
  }
  for (const l of listeners) l()
}

export function useGameScope(): GameScope {
  const { wargames } = useSession()
  const scope = useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => current,
    () => 'all' as const,
  )
  // With wargames off there is nothing to switch between, whatever this browser remembered.
  return wargames ? scope : 'all'
}

/** The `game` query parameter for list endpoints: absent when browsing everything. */
export const gameParam = (scope: GameScope) => (scope === 'all' ? undefined : scope)
